/**
 * SM-1165: shared classification/parsing for meeting-link clicks (calendar
 * items, the Schedule modal's join link, and the post-schedule invitation
 * link).
 *
 * The user-facing behaviour (special clicks, new/join meeting, hosted popup /
 * fallback) is documented in `docs/MEETING_LINK_UX.md`.
 *
 * Links to the Meet instance we are running from should reuse the existing
 * meeting-launch path — see `useOpenMeetingLink` — so they get the hosted popup
 * / existing-window focus / popup-blocked fallback / standalone in-page
 * navigation behavior instead of a plain browser navigation. Every other link
 * (Zoom, Teams, legacy meeting hosts, etc.) opens in a new tab, the same in
 * TeamHub desktop as in a regular browser.
 *
 * SM-1282: "ours" is decided by ORIGIN, not by the `meetings.sangoma.com`
 * domain. The launcher can only ever open a room on our own origin, so
 * accepting sibling deployments (`meetings.sangoma.com` while we are running
 * from `develop.meetings.sangoma.com`) silently joined the right room id on the
 * wrong instance. Other Meet deployments are external links like any other.
 */

import { resolveBundleOrigin } from '../config/appOrigin';
import { isHostedByTeamHub } from '../services/hostAuthService';
import { getBaseUrl } from './url';

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * The origins that belong to *this* Meet instance — the ones the launcher can
 * actually open a room on.
 *
 * Hosted mode is deliberately strict: the page origin is TeamHub's, and
 * `getBaseUrl()` falls back to it, so the bundle's public path is the only
 * trustworthy signal. When it can't be resolved we return nothing rather than
 * guessing — a popup would load the host origin, which has no Meet page, so
 * handing the link out externally is the better outcome.
 */
function getOwnMeetOrigins(): string[] {
  const origins: string[] = [];
  const add = (origin: string | null | undefined): void => {
    if (origin && !origins.includes(origin)) {
      origins.push(origin);
    }
  };

  if (isHostedByTeamHub()) {
    add(resolveBundleOrigin());
    return origins;
  }

  // Standalone: links are built from `getBaseUrl()` (VITE_REDIRECT_URI in dev),
  // and same-origin links are always safe to route in-page.
  add(originOf(getBaseUrl()));
  if (typeof window !== 'undefined') {
    add(window.location?.origin);
  }
  return origins;
}

/**
 * True when `url` points at the Meet instance this bundle was served from, and
 * so should be routed through the in-app meeting launcher. Returns `false` for
 * unparseable input (fail closed — falls back to a plain new-tab open).
 */
export function isCurrentMeetInstanceUrl(url: string): boolean {
  const origin = originOf(url);
  return origin !== null && getOwnMeetOrigins().includes(origin);
}

export interface ParsedMeetingLink {
  meetingId: string;
  password?: string;
}

/**
 * Extract the meeting id (first path segment) and passcode (hash fragment,
 * matching the format produced by `buildMeetingPath`) from a Sangoma Meet
 * URL. Returns `null` for malformed or room-less URLs so callers can safely
 * fall back to a plain new-tab open instead of navigating the host.
 */
export function parseSangomaMeetingUrl(url: string): ParsedMeetingLink | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const [firstSegment] = parsed.pathname.split('/').filter(Boolean);
  if (!firstSegment) {
    return null;
  }

  let meetingId: string;
  try {
    meetingId = decodeURIComponent(firstSegment);
  } catch {
    meetingId = firstSegment;
  }
  if (!meetingId) {
    return null;
  }

  const rawPassword = parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash;
  let password: string | undefined;
  if (rawPassword) {
    try {
      password = decodeURIComponent(rawPassword);
    } catch {
      password = rawPassword;
    }
  }

  return { meetingId, password };
}

/** Open a non-Sangoma link in a new tab; browser and TeamHub desktop both use their standard tab/window behavior. */
export function openExternalMeetingLink(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** The subset of a (React or native) mouse event needed to detect a plain left click. */
export interface ClickModifiers {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/**
 * True for an unmodified left click — i.e. one that should be intercepted and
 * routed in-app. Cmd/Ctrl/Shift/Alt-click and middle-click are left alone so
 * the browser's native "open in new tab/window" behavior keeps working.
 */
export function isPlainLeftClick(event: ClickModifiers): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}
